import { useState } from 'react';
import { t } from '../../i18n';

export type ShareResult = { readonly url: string } | { readonly error: string };

/** A link with Copy, and Share where the device can share (phones). */
export function ShareLink({ url, title }: { url: string; title?: string }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  return (
    <div className="share-link">
      <input
        type="text"
        readOnly
        value={url}
        aria-label={t('share.linkLabel')}
        onFocus={(e) => e.currentTarget.select()}
      />
      <button
        type="button"
        className="btn btn-small"
        onClick={() => {
          void navigator.clipboard?.writeText(url).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
      >
        {copied ? t('share.copied') : t('share.copy')}
      </button>
      {canShare && (
        <button
          type="button"
          className="btn btn-small"
          onClick={() => {
            void navigator.share({ title: title ?? t('app.title'), url }).catch(() => undefined);
          }}
        >
          {t('share.share')}
        </button>
      )}
    </div>
  );
}

/** A button that makes a link (a request to the site), then shows it. */
export function ShareButton({
  create,
  label,
}: {
  create: () => Promise<ShareResult>;
  label: string;
}) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'done'; url: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  if (state.kind === 'done') return <ShareLink url={state.url} />;
  return (
    <>
      <button
        type="button"
        className="btn"
        disabled={state.kind === 'busy'}
        onClick={() => {
          setState({ kind: 'busy' });
          void create().then((r) =>
            setState(
              'url' in r ? { kind: 'done', url: r.url } : { kind: 'error', message: r.error },
            ),
          );
        }}
      >
        {state.kind === 'busy' ? t('share.making') : label}
      </button>
      {state.kind === 'error' && (
        <p className="notice notice-error" role="alert">
          {state.message}
        </p>
      )}
    </>
  );
}
